/* ============================================================
   HumaGreen — moteur de panier
   Tiroir panier, ajout de pack en un clic, barre de progression
   vers le port offert, relance consommables, alerte seuil cassé.
   Aucune dépendance. Module ES natif.
   ============================================================ */

const SELECTEURS = {
  etat:        '#hg-etat-panier',
  progression: '[data-hg-progression]',
  relance:     '[data-hg-relance]',
  alerte:      '[data-hg-alerte-seuil]',
  tiroir:      '#hg-tiroir',
  contenu:     '[data-hg-tiroir-contenu]',
};

/* Consommables qui n'alourdissent pas la tranche de port.
   Gourde 350 g + charbon 7 g + membrane 55 g = 412 g,
   soit toujours la tranche 250-500 g. */
const CONSOMMABLES = ['charbon-actif-remplacement', 'membrane-ultrafiltrante-remplacement'];
const PRODUIT_PHARE = 'gourde-filtrante';

const racine = () => window.Shopify?.routes?.root || '/';

function lireEtatInitial() {
  const noeud = document.querySelector(SELECTEURS.etat);
  if (!noeud) return null;
  try { return JSON.parse(noeud.textContent); } catch { return null; }
}

function formater(centimes, devise = 'EUR') {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: devise, minimumFractionDigits: 2,
  }).format(centimes / 100);
}

async function recupererPanier() {
  const reponse = await fetch(`${racine()}cart.js`, { headers: { Accept: 'application/json' } });
  if (!reponse.ok) throw new Error(`cart.js a répondu ${reponse.status}`);
  return reponse.json();
}

/* ---------- Barre de progression ----------
   Plusieurs exemplaires possibles : le tiroir et la page panier
   peuvent être présents en même temps. */
function rendreProgression(panier, seuil) {
  document.querySelectorAll(SELECTEURS.progression).forEach((cible) => {
    /* L'argument sur les consommables n'a de sens que si la gourde ou un
       consommable est au panier (pas pour un panier de boîtes à savon). */
    const refs = panier.items.map((article) => article.handle);
    const pertinent = refs.includes(PRODUIT_PHARE) || CONSOMMABLES.some((ref) => refs.includes(ref));
    const argument = pertinent ? (cible.dataset.argument || '') : '';
    const total = panier.total_price;
    const reste = Math.max(0, seuil - total);
    const atteint = reste === 0;
    const pourcentage = Math.min(100, Math.round((total / seuil) * 100));

    cible.classList.toggle('hg-progression--atteint', atteint);
    cible.innerHTML = `
      <p class="hg-progression__texte">
        ${atteint
          ? '<strong>Livraison offerte en point relais.</strong>'
          : `Plus que <strong>${formater(reste, panier.currency)}</strong> pour la livraison offerte en point relais.`}
      </p>
      <div class="hg-progression__piste" role="progressbar"
           aria-valuenow="${pourcentage}" aria-valuemin="0" aria-valuemax="100"
           aria-label="Progression vers la livraison offerte">
        <div class="hg-progression__jauge" style="width:${pourcentage}%"></div>
      </div>
      ${atteint || !argument ? '' : `<p class="hg-progression__argument">${argument}</p>`}
    `;
  });
}

/* ---------- Relance consommables ----------
   Ne se déclenche que si la gourde est au panier SANS consommable.
   On ne relance jamais sur un panier qui en contient déjà. */
function rendreRelance(panier, seuil) {
  const references = panier.items.map((article) => article.handle);
  const aLaGourde = references.includes(PRODUIT_PHARE);
  const aUnConsommable = CONSOMMABLES.some((ref) => references.includes(ref));
  const montrer = aLaGourde && !aUnConsommable;

  document.querySelectorAll(SELECTEURS.relance).forEach((cible) => {
    cible.hidden = !montrer;

    /* Phrase de livraison : calculée sur le total réel du panier (remises comprises),
       ne promet la gratuité que si l'ajout franchit réellement le seuil. */
    const note = cible.querySelector('[data-hg-relance-port]');
    const ajout = Number(cible.dataset.hgPrixAjout) || 0;
    if (!note) return;
    if (!montrer || ajout <= 0) { note.hidden = true; return; }

    const apres = panier.total_price + ajout;
    note.textContent = apres >= seuil
      ? `Avec les deux, votre commande passe à ${formater(apres, panier.currency)} : livraison offerte en point relais.`
      : `Avec les deux, il ne vous manquerait plus que ${formater(seuil - apres, panier.currency)} pour la livraison offerte.`;
    note.hidden = false;
  });
}

/* ---------- Alerte seuil cassé par un code promo ----------
   Cas réel : la gourde est en promotion à 42,41 € et le seuil est à
   49,90 €. Un code de réduction peut faire repasser sous le seuil et
   le client perd la gratuité sans être prévenu avant le paiement. */
function rendreAlerteSeuil(panier, seuil) {
  const remise = panier.total_discount || 0;
  const avantRemise = panier.total_price + remise;
  const casse = remise > 0 && avantRemise >= seuil && panier.total_price < seuil;

  document.querySelectorAll(SELECTEURS.alerte).forEach((cible) => {
    cible.hidden = !casse;
    if (!casse) return;
    const manque = seuil - panier.total_price;
    cible.textContent =
      `Votre réduction fait passer le panier sous ${formater(seuil, panier.currency)} : `
      + `la livraison offerte ne s'applique plus. Il manque ${formater(manque, panier.currency)}.`;
  });
}

/* ---------- Compteur d'articles dans l'en-tête ---------- */
function rendreCompteur(panier) {
  document.querySelectorAll('[data-hg-nb-articles]').forEach((n) => {
    n.textContent = panier.item_count;
  });
}

/* ---------- Tiroir ----------
   Le contenu est re-rendu par Shopify via l'API de rendu de section :
   aucun prix, aucune remise et aucun libellé n'est recalculé ici. */
const tiroir = () => document.querySelector(SELECTEURS.tiroir);

function ouvrirTiroir() {
  const t = tiroir();
  if (!t) return;
  if (!t.open) t.showModal();
}

function fermerTiroir() {
  const t = tiroir();
  if (t?.open) t.close();
}

async function rafraichirTiroir() {
  const t = tiroir();
  if (!t) return;
  try {
    const reponse = await fetch(`${racine()}?sections=tiroir-panier`, {
      headers: { Accept: 'application/json' },
    });
    if (!reponse.ok) throw new Error(`rendu de section : ${reponse.status}`);
    const donnees = await reponse.json();
    const html = new DOMParser().parseFromString(donnees['tiroir-panier'], 'text/html');
    const neuf = html.querySelector(SELECTEURS.contenu);
    const actuel = t.querySelector(SELECTEURS.contenu);
    if (neuf && actuel) {
      actuel.innerHTML = neuf.innerHTML;
      /* Le contenu re-rendu repart avec une barre de progression vide :
         on redemande un calcul après la greffe, pas avant. */
      document.dispatchEvent(new CustomEvent('hg:panier:demande-maj'));
    }
  } catch (erreur) {
    console.warn('[HumaGreen] tiroir non rafraîchi :', erreur.message);
  }
}

/* ---------- Mutations ---------- */
async function ajouter(articles) {
  const reponse = await fetch(`${racine()}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: articles }),
  });
  if (!reponse.ok) {
    const erreur = await reponse.json().catch(() => ({}));
    throw new Error(erreur.description || erreur.message || `ajout refusé (${reponse.status})`);
  }
  return reponse.json();
}

async function changerLigne(ligne, quantite) {
  const reponse = await fetch(`${racine()}cart/change.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ line: Number(ligne), quantity: Number(quantite) }),
  });
  if (!reponse.ok) throw new Error(`modification refusée (${reponse.status})`);
  return reponse.json();
}

/* Une formule porte ses références au format « id:quantité,id:quantité ». */
function analyserReferences(chaine) {
  const articles = String(chaine || '')
    .split(',')
    .map((paire) => {
      const [id, quantite] = paire.split(':');
      return { id: Number(id), quantity: Number(quantite) || 1 };
    })
    .filter((article) => Number.isFinite(article.id) && article.id > 0);

  return articles.length ? articles : null;
}

function lireFormule(formulaire) {
  const coche = formulaire.closest('.hg-produit__achat')
    ?.querySelector('[data-hg-pack-references]:checked');
  return coche ? analyserReferences(coche.dataset.hgPackReferences) : null;
}

function signalerErreur(formulaire, message) {
  let zone = formulaire.querySelector('[data-hg-erreur]');
  if (!zone) {
    zone = document.createElement('p');
    zone.className = 'hg-alerte';
    zone.setAttribute('data-hg-erreur', '');
    zone.setAttribute('role', 'alert');
    formulaire.appendChild(zone);
  }
  zone.textContent = message;
}

/* ---------- Orchestration ---------- */
async function rafraichir(reglages) {
  try {
    const panier = await recupererPanier();
    if (reglages.afficherProgression) rendreProgression(panier, reglages.seuil);
    rendreRelance(panier, reglages.seuil);
    if (reglages.alerterSeuilCasse) rendreAlerteSeuil(panier, reglages.seuil);
    rendreCompteur(panier);
    document.dispatchEvent(new CustomEvent('hg:panier:maj', { detail: panier }));
  } catch (erreur) {
    /* Un panier injoignable ne doit jamais casser la page. */
    console.warn('[HumaGreen] panier indisponible :', erreur.message);
  }
}

function brancherInteractions(reglages) {
  /* Ouverture du tiroir depuis l'en-tête. Sans JavaScript, le lien
     mène à /cart : le parcours reste complet. */
  document.addEventListener('click', (evenement) => {
    const lien = evenement.target.closest('.hg-entete__panier');
    if (lien && tiroir()) {
      evenement.preventDefault();
      rafraichirTiroir().then(ouvrirTiroir);
    }

    if (evenement.target.closest('[data-hg-tiroir-fermer]')) fermerTiroir();

    const pas = evenement.target.closest('[data-hg-quantite]');
    if (pas) {
      pas.disabled = true;
      changerLigne(pas.dataset.hgQuantite, pas.dataset.hgValeur)
        .then(rafraichirTiroir)
        .catch((e) => console.warn('[HumaGreen]', e.message));
    }

    /* Offre d'article : le pack entier en un clic, sans quitter la lecture. */
    const pack = evenement.target.closest('[data-hg-ajouter-pack]');
    if (pack) {
      const articles = analyserReferences(pack.dataset.hgAjouterPack);
      if (!articles) return;
      const libelle = pack.textContent;
      pack.disabled = true;
      pack.textContent = 'Ajout en cours…';
      ajouter(articles)
        .then(rafraichirTiroir)
        .then(ouvrirTiroir)
        .catch((e) => signalerErreur(pack.closest('.hg-offre__actions') || pack.parentElement, e.message))
        .finally(() => { pack.disabled = false; pack.textContent = libelle; });
      return;
    }

    const ajout = evenement.target.closest('[data-hg-ajouter]');
    if (ajout) {
      ajout.disabled = true;
      ajouter([{ id: Number(ajout.dataset.hgAjouter), quantity: 1 }])
        .then(rafraichirTiroir)
        .catch((e) => console.warn('[HumaGreen]', e.message))
        .finally(() => { ajout.disabled = false; });
    }
  });

  /* Clic sur le fond du tiroir : on ferme, comme partout ailleurs. */
  const t = tiroir();
  if (t) {
    t.addEventListener('click', (evenement) => {
      if (evenement.target === t) fermerTiroir();
    });
  }

  /* Ajout au panier : une formule peut contenir plusieurs références,
     on les envoie en un seul appel plutôt qu'en cascade. */
  const formulaire = document.querySelector('#hg-formulaire-achat');
  if (formulaire) {
    formulaire.addEventListener('submit', async (evenement) => {
      /* La quantité du sélecteur multiplie chaque ligne : deux packs démarrage,
         ce sont deux gourdes, deux charbons et deux membranes. */
      const quantite = Math.max(1, Math.min(99, parseInt(formulaire.elements.quantity?.value, 10) || 1));
      const formule = lireFormule(formulaire);
      const articles = formule
        ? formule.map((article) => ({ ...article, quantity: article.quantity * quantite }))
        : [{ id: Number(formulaire.querySelector('[data-hg-variante]')?.value), quantity: quantite }];
      if (!articles[0]?.id) return; /* on laisse le POST classique faire le travail */

      /* Complément coché (boîte à savon) : une seule unité, jamais multipliée. */
      const complement = formulaire.querySelector('[data-hg-complement]:checked');
      if (complement) articles.push({ id: Number(complement.dataset.hgComplement), quantity: 1 });

      evenement.preventDefault();
      const bouton = formulaire.querySelector('button[type="submit"]');
      const libelle = bouton?.textContent;
      if (bouton) { bouton.disabled = true; bouton.textContent = 'Ajout en cours…'; }

      try {
        await ajouter(articles);
        await rafraichirTiroir();
        ouvrirTiroir();
      } catch (erreur) {
        signalerErreur(formulaire, erreur.message);
      } finally {
        if (bouton) { bouton.disabled = false; bouton.textContent = libelle; }
      }
    });
  }
}

function initialiser() {
  const etat = lireEtatInitial();
  if (!etat) return;

  const reglages = {
    seuil: etat.seuil_port || 4990,
    afficherProgression: true,
    alerterSeuilCasse: true,
  };

  rafraichir(reglages);
  brancherInteractions(reglages);

  /* Toute mutation du panier passe par ces routes : on se branche dessus
     plutôt que de dupliquer la logique dans chaque bouton. */
  const routesPanier = /\/cart\/(add|change|update|clear)(\.js)?$/;
  const fetchOrigine = window.fetch;
  window.fetch = async function (...args) {
    const reponse = await fetchOrigine.apply(this, args);
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    if (routesPanier.test(url) && reponse.ok) rafraichir(reglages);
    return reponse;
  };

  document.addEventListener('hg:panier:demande-maj', () => rafraichir(reglages));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialiser, { once: true });
} else {
  initialiser();
}
